import { Component, effect, HostListener, inject, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbarComponent } from './app.topbar';
import { LayoutService } from '@layout/service/layout.service';
import { AppConfiguratorComponent } from './app.configurator';
import { AppBreadcrumbComponent } from './app.breadcrumb';
import { AppSidebarComponent } from './app.sidebar';
import { ToastModule } from 'primeng/toast';

import { AppFooterComponent } from './app.footer';
import { SupportFloatingWidgetComponent } from '@shared/components/support-floating-widget/support-floating-widget.component';
import { PullToRefreshComponent } from '@shared/components/pull-to-refresh';
import { isCapacitor } from '@shared/utils/platform.utils';
import { CommandPaletteComponent } from '@core/command-palette/command-palette.component';
import { CommandPaletteService } from '@core/command-palette/command-palette.service';
import { AuthService } from '@auth/services/auth.service';
import { NotificationStoreService } from '@core/notifications/notification-store.service';
import { NotificationRealtimeService } from '@core/notifications/notification-realtime.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    AppTopbarComponent,
    AppSidebarComponent,
    RouterModule,
    AppConfiguratorComponent,
    AppBreadcrumbComponent,
    ToastModule,
    AppFooterComponent,
    SupportFloatingWidgetComponent,
    CommandPaletteComponent,
    PullToRefreshComponent,
  ],
  template: `
    <div class="layout-container" [ngClass]="containerClass">
      @if (!layoutService.isHorizontal()) {
        <div app-sidebar></div>
      }
      <div class="layout-content-wrapper">
        @if (isNativeApp) {
          <app-pull-to-refresh />
        }

        <div app-topbar></div>

        <div class="content-breadcrumb">
          <div app-breadcrumb></div>
        </div>

        <div class="layout-content">
          <div>
            <router-outlet></router-outlet>
          </div>
          <app-footer></app-footer>
        </div>

        <div class="layout-mask"></div>
      </div>
      <app-configurator />
      <shared-support-floating-widget />
      <app-command-palette />
    </div>
  `,
})
export class AppLayoutComponent implements OnDestroy {
  overlayMenuOpenSubscription: Subscription;

  menuOutsideClickListener: (() => void) | null = null;

  menuScrollListener: (() => void) | null = null;

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

  @ViewChild(AppTopbarComponent) appTopbar!: AppTopbarComponent;

  public layoutService = inject(LayoutService);
  public renderer = inject(Renderer2);
  public router = inject(Router);

  /**
   * El gesto de pull-to-refresh solo existe en la app nativa: en web el navegador ya
   * tiene su propio refresco (F5 o el pull-to-refresh de Chrome móvil) y los dos
   * gestos pelearían por el mismo arrastre.
   */
  protected readonly isNativeApp = isCapacitor();
  private readonly commandPalette = inject(CommandPaletteService);
  private readonly auth = inject(AuthService);
  private readonly notificationStore = inject(NotificationStoreService);
  private readonly notificationRealtime = inject(NotificationRealtimeService);

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.commandPalette.toggle();
    }
  }

  constructor() {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          if (this.isOutsideClicked(event)) {
            this.hideMenu();
          }
        });
      }
      if (
        (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus()) &&
        !this.menuScrollListener
      ) {
        const menuContainer = this.layoutService.isHorizontal()
          ? this.appTopbar.appSidebar?.appMenu?.menuContainer?.nativeElement
          : this.appSidebar?.appMenu?.menuContainer?.nativeElement;

        if (menuContainer) {
          this.menuScrollListener = this.renderer.listen(menuContainer, 'scroll', () => {
            if (this.layoutService.isDesktop()) {
              this.hideMenu();
            }
          });
        }
      }
      if (this.layoutService.layoutState().staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();
    });

    // Ciclo de vida de las notificaciones en tiempo real, atado al estado de sesión:
    // al autenticarse arranca SignalR y sincroniza el badge; al cerrar sesión, corta y limpia.
    effect(() => {
      const status = this.auth.authStatus();
      if (status === 'authenticated') {
        void this.notificationRealtime.start();
        this.notificationStore.refreshUnreadCount();
      } else if (status === 'not-authenticated') {
        void this.notificationRealtime.stop();
        this.notificationStore.reset();
      }
    });
  }

  isOutsideClicked(event: Event) {
    return !isClickInsideMenuSurface(event.target as Node | null);
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' '
      );
    }
  }

  hideMenu() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    this.layoutService.reset();
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }
  get containerClass() {
    const layoutConfig = this.layoutService.layoutConfig();
    const layoutState = this.layoutService.layoutState();

    return {
      'layout-light': !layoutConfig.darkTheme,
      'layout-dark': layoutConfig.darkTheme,
      'layout-overlay': layoutConfig.menuMode === 'overlay',
      'layout-static': layoutConfig.menuMode === 'static',
      'layout-slim': layoutConfig.menuMode === 'slim',
      'layout-slim-plus': layoutConfig.menuMode === 'slim-plus',
      'layout-horizontal': layoutConfig.menuMode === 'horizontal',
      'layout-reveal': layoutConfig.menuMode === 'reveal',
      'layout-drawer': layoutConfig.menuMode === 'drawer',
      'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
      'layout-overlay-active': layoutState.overlayMenuActive,
      'layout-mobile-active': layoutState.staticMenuMobileActive,
      'layout-sidebar-active': layoutState.sidebarActive,
      'layout-sidebar-anchored': layoutState.anchored,
    };
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}

/**
 * Selectores de los overlays de PrimeNG que se renderizan en `body`: aunque
 * cuelguen fuera del sidebar, visualmente forman parte del menú (el selector de
 * módulos en móvil, y los selects o datepickers que se abran dentro).
 */
const PRIMENG_OVERLAY_SELECTORS = '.p-popover, .p-overlay, .p-select-overlay, .p-datepicker-panel, .p-menu-overlay';

/**
 * ¿El clic ocurrió sobre alguna superficie que cuenta como "el menú"?
 *
 * Sin la comprobación de overlays, tocar el popover del selector de módulos se
 * interpretaba como un clic fuera y cerraba el menú móvil entero antes de que el
 * usuario pudiera elegir. Se exporta aparte para poder probarlo sin montar todo
 * el layout.
 */
export function isClickInsideMenuSurface(target: Node | null): boolean {
  if (!target) return false;

  const sidebarEl = document.querySelector('.layout-sidebar');
  const topbarButtonEl = document.querySelector('.topbar-menubutton');

  if (
    sidebarEl?.isSameNode(target) ||
    sidebarEl?.contains(target) ||
    topbarButtonEl?.isSameNode(target) ||
    topbarButtonEl?.contains(target)
  ) {
    return true;
  }

  const element = target instanceof Element ? target : target.parentElement;
  return !!element?.closest(PRIMENG_OVERLAY_SELECTORS);
}
